/**
 * On the ground (ROUND7 §5 stations 2-3): moving, aiming, running and crawling where he lands; the pod he
 * kicked; the crouched walk and the silent knife. Each is ticked from what the world shows, so it does not
 * matter which key he pressed or in what order he did them.
 */

import { COURSE } from '../../gen/training';
import { angleDiff } from '../../sim/math';
import { STEALTH } from '../../sim/tuningStealth';
import type { Enemy, Pod, World } from '../../sim/types';
import { isAware } from './facts';
import { holdOrPress } from './keys';
import { bearing, onGround, type Step } from './stepTypes';

/** The basics are taught where he lands: once he is up among the sentries they stop being mentioned. */
const inLandingArea = (w: World): boolean => onGround(w) && w.ranger.z > COURSE.podClearing.z0;

/** He has to come this close to a pod before the coach sends him to it; further, it waits its turn. */
const POD_CALL_M = 45;

export function nearestPod(w: World): { pod: Pod; distance: number } | null {
  const r = w.ranger;
  let best: { pod: Pod; distance: number } | null = null;
  for (const pod of w.insertion.pods) {
    if (pod.state !== 'landed') continue;
    const distance = Math.sqrt((pod.x - r.x) ** 2 + (pod.z - r.z) ** 2);
    if (best === null || distance < best.distance) best = { pod, distance };
  }
  return best;
}

/** The two knife sentries, by their posts; either may be gone (dead, wounded) or on the alert. */
export function knifeSentries(w: World): Enemy[] {
  const { a, b } = COURSE.knife;
  return w.enemies.filter((e) => (e.homeX === a.x && e.homeZ === a.z) || (e.homeX === b.x && e.homeZ === b.z));
}

/** Still standing, and has not noticed him: a man he can still take silently. */
export const takeable = (e: Enemy): boolean => e.state !== 'dead' && e.state !== 'wounded' && !isAware(e);

const move: Step = {
  id: 'move', station: 'ON THE GROUND', title: 'MOVE',
  text: ({ keys }) => `MOVE: ${(['moveUp', 'moveLeft', 'moveDown', 'moveRight'] as const).map(keys.key).join(' ')}.`,
  note: () => 'YOU ARE DOWN. THE MISSION CLOCK, TOP LEFT, HAS STARTED. THE COURSE RUNS NORTH, UP THE SCREEN.',
  ready: ({ world }) => inLandingArea(world),
  done: ({ world, facts }) => facts.landed !== null && world.ranger.travelled - facts.landed.travelled >= 4,
};

const aim: Step = {
  id: 'aim', station: 'ON THE GROUND', title: 'AIM',
  text: ({ keys }) => (keys.classic
    ? 'AIM: YOU POINT THE WAY YOU FACE - TURN WITH THE DIRECTION KEYS.'
    : 'AIM WITH THE MOUSE: YOU TURN TO FACE THE CURSOR, AND YOUR WEAPON POINTS AT IT.'),
  note: () => 'WALK AWAY FROM WHERE YOU AIM AND YOU BACK OFF, STILL FACING IT - SLOWER.',
  ready: ({ world }) => inLandingArea(world),
  done: ({ world, facts }) => facts.landed !== null && Math.abs(angleDiff(facts.landed.aim, world.ranger.aim)) >= 0.8,
};

const run: Step = {
  id: 'run', station: 'ON THE GROUND', title: 'RUN',
  text: ({ keys }) => `RUN: ${holdOrPress(keys, 'run')} WHILE YOU MOVE.`,
  note: () => 'RUNNING IS LOUD AND IT TIRES YOU: THE BAR BOTTOM LEFT FILLS. WINDED, YOU CANNOT RUN AT ALL.',
  ready: ({ world }) => inLandingArea(world),
  done: ({ facts }) => facts.runTicks >= 40,
};

const crawl: Step = {
  id: 'crawl', station: 'ON THE GROUND', title: 'CRAWL',
  text: ({ keys }) => (keys.crawlToggle
    ? `CRAWL: PRESS ${keys.key('crawl')} - AND PRESS IT AGAIN TO STAND UP.`
    : `CRAWL: HOLD ${keys.key('crawl')}. LET GO TO STAND UP.`),
  note: () => 'LYING DOWN YOU ARE SLOW, BUT QUIET AND HARD TO SEE.',
  ready: ({ world }) => inLandingArea(world),
  done: ({ facts }) => facts.stoodAfterCrawl,
};

const pod: Step = {
  id: 'pod', station: 'SUPPLY POD', title: 'OPEN A POD',
  text: ({ world, keys }) => {
    const near = nearestPod(world);
    if (near === null) return `OPEN YOUR POD: WALK UP TO IT AND PRESS ${keys.key('interact')}.`;
    const letter = 'ABC'.charAt(near.pod.index);
    const r = world.ranger;
    const way = bearing(near.pod.x - r.x, near.pod.z - r.z);
    const where = near.distance < 3 ? 'HERE' : `${Math.round(near.distance)} M ${way}`;
    return `OPEN POD ${letter} (${where}): WALK UP TO IT AND PRESS ${keys.key('interact')}.`;
  },
  note: () => 'YOU TAKE WHAT YOU CAN CARRY; THE REST STAYS IN THE POD. THE STRIP MAP MARKS EVERY POD.',
  ready: ({ world }) => {
    const near = onGround(world) ? nearestPod(world) : null;
    return near !== null && near.distance <= POD_CALL_M;
  },
  done: ({ facts }) => facts.podOpened,
};

const sneak: Step = {
  id: 'sneak', station: 'THE KNIFE', title: 'SNEAK',
  text: ({ keys }) => `SNEAK: ${holdOrPress(keys, 'sneak')} - A CROUCHED WALK. SLOWER THAN WALKING, AND ALMOST SILENT.`,
  note: () => `A MAN WITH HIS BACK TO YOU HEARS A WALKER ${STEALTH.hear.walk} M AWAY, A RUNNER `
    + `${STEALTH.hear.run} M - AND A SNEAKER ONLY ${STEALTH.hear.sneak} M.`,
  ready: ({ world }) => onGround(world),
  done: ({ facts }) => facts.sneakTicks >= 40,
};

const knife: Step = {
  id: 'knife', station: 'THE KNIFE', title: 'A SILENT KNIFE KILL',
  text: ({ world, keys }) => {
    const [a] = knifeSentries(world).filter((e) => e.homeX === COURSE.knife.a.x);
    const how = `${keys.key('weapon5')} THEN ${keys.key('fire')}, OR ${keys.key('quickKnife')} WITH ANY WEAPON`;
    return a !== undefined && takeable(a)
      ? `SNEAK UP BEHIND THE SENTRY AHEAD AND KNIFE HIM: ${how}.`
      : `TRY AGAIN: THE SENTRY TO THE WEST. SNEAK UP BEHIND HIM AND KNIFE HIM: ${how}.`;
  },
  note: ({ facts }) => (facts.loudKills > 0
    ? 'HE KNEW YOU WERE THERE, SO HE CRIED OUT AS HE FELL. COME IN SLOW AND LOW, FROM BEHIND.'
    : 'HIS BACK IS TO YOU. A MAN WHO NEVER KNEW YOU WERE THERE DIES WITHOUT A SOUND.'),
  ready: ({ world }) => onGround(world) && world.ranger.z > COURSE.knife.passedZ && knifeSentries(world).some(takeable),
  done: ({ facts }) => facts.silentKills > 0,
};

export const GROUND_STEPS: readonly Step[] = [move, aim, run, crawl, pod, sneak, knife];
